import React from 'react';
import { Tooltip } from 'antd';
import { FJ180 } from '../../../services/j209Api';
import { Mode } from '../hooks/useJ209Mode';

interface J209Permissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: FJ180 | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: J209Permissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onPrint: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const isApproved = (record: FJ180 | null): boolean => {
  return record?.fj180_80 === 'P';
};

const ActionBar: React.FC<ActionBarProps> = ({
  mode, isEditing, record,
  currentIdx, allIdsLength,
  permissions,
  onAdd, onModify, onDelete, onPrint, onNavTo, onExit,
}) => {
  const { canView, canCreate, canEdit, canDelete, canPrint } = permissions;
  const canSave = mode === 'add' ? canCreate : canEdit;
  const locked = isApproved(record);

  return (
    <div className="action-bar">
      <div className="action-group">
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(0)}>最前</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)}>上一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(currentIdx + 1)}>下一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(allIdsLength - 1)}>最後</PermBtn>
      </div>

      <div className="action-group">
        <PermBtn
          allowed={isEditing ? canSave : canCreate}
          disabled={false}
          className="btn btn-primary"
          onClick={onAdd}
        >
          {isEditing ? '存檔' : '新增'}
        </PermBtn>
        <PermBtn
          allowed={isEditing ? true : canEdit}
          disabled={(!isEditing && !record) || (!isEditing && locked)}
          onClick={onModify}
        >
          {isEditing ? '還原' : '修改'}
        </PermBtn>
        <PermBtn
          allowed={canDelete}
          disabled={isEditing || !record || locked}
          className="btn btn-danger"
          onClick={onDelete}
        >
          刪除
        </PermBtn>

        <div className="action-separator" />

        <PermBtn allowed={canPrint} disabled={isEditing || !record} onClick={onPrint}>列印</PermBtn>

        <button className="btn" onClick={onExit}>離開</button>
      </div>

      {locked && (
        <span style={{
          marginLeft: 12, padding: '2px 10px', borderRadius: 4,
          fontWeight: 'bold', color: '#389e0d',
          background: '#f6ffed', border: '1px solid #b7eb8f',
        }}>
          已核准
        </span>
      )}
    </div>
  );
};

export default React.memo(ActionBar);
